import React, { useState, useEffect } from 'react';
import BitaButton from '../../../../../../bitaComponents/BitaButton/BitaButton';
import BitaModal from '../../../../../../bitaComponents/BitaModal/BitaModal';
import IndexSlider from '../SliderTickers/IndexSlider';
import styles from './preview-summary-modal.module.scss';
import style from '../../../../PortfolioBasketCalculation.module.scss';
import './PreviewSummaryModal.scss';
import ShowPreview from '../../../../utils/ShowPreview';
import ShowAdditionalParametersPreview from '../../../../utils/ShowAdditionalParametersPreview';
import BitaSpinner from '../../../../../../components/BitaSpinner/BitaSpinner';
import SummaryTables from './SummaryTables';
import { CALCULATION_ZONE_CONFIG } from '../../../../utils/constants';
import { getZoneValue } from '../../../../utils';

const INITIAL_INPUTS = 'initial-inputs';
const PARAMETERS = 'parameters';
const ADDITIONAL_PARAMETERS = 'additional-parameters';
const IDENTIFIER_TYPES = {
  ISIN: 'isin',
  SEDOL: 'sedol',
  TYPE: 'type',
};

const PreviewSummaryModal = ({
  isSubmitBasket,
  existsCashConstituentsInMetadata,
  showModal,
  closeModal,
  onConfirm,
  selectedBasketsData,
  updatedBasketsData,
  previousBasketsData,
  withholdingTaxTemplateData,
  calculationZoneMap,
}) => {
  const [selectedTab, setSelectedTab] = useState(INITIAL_INPUTS);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (selectedBasketsData?.length > 0) {
      setSelectedIndex(selectedBasketsData[0].symbol);
    }
  }, [selectedBasketsData]);

  const handleSetSelectedIndex = index => {
    setSelectedIndex(index);
  };

  const handleConfirm = () => {
    setIsLoading(true);
    onConfirm();
  };

  const handleCancel = () => {
    closeModal();
  };

  const handleCloseModal = () => {
    if (isLoading) {
      return;
    }
    closeModal();
  };

  const selectedBasket = selectedBasketsData?.find(basket => {
    return basket.symbol === selectedIndex;
  });

  const selectedUpdatedBasket = updatedBasketsData?.find(basket => {
    return basket.symbol === selectedIndex;
  });

  const selectedPreviousBasket = previousBasketsData?.find(basket => {
    return basket.symbol === selectedIndex;
  });

  const buildParametersToShow = basket => ({
    symbol: basket?.symbol,
    orgPrefix: basket?.org_prefix || '',
    name: basket?.name,
    description: basket?.description,
    divisor_determination_setup:
      basket?.divisor_determination_setup ??
      basket?.divisor_determination_point ??
      basket?.additional_settings?.divisor_determination_setup,
    divisor: basket?.divisor,
    currency: basket?.currency,
    return_type: basket?.calculation_type,
    withholding_tax_id: basket?.withholding_tax_id || 'BITA Withholding Taxes',
    calculation_setup: basket?.calculation_setup,
    calculation_zone: basket?.calculation_zone,
    effective_date: basket?.effective_date,
    calculation_open_time: getZoneValue(
      calculationZoneMap,
      basket,
      CALCULATION_ZONE_CONFIG.CALC_OPEN_TIME,
    ),
    calculation_close_time: getZoneValue(
      calculationZoneMap,
      basket,
      CALCULATION_ZONE_CONFIG.CALC_CLOSE_TIME,
    ),
    calculation_start_week_day: getZoneValue(
      calculationZoneMap,
      basket,
      CALCULATION_ZONE_CONFIG.CALC_START_WEEK_DAY,
      true,
    ),
    calculation_end_week_day: getZoneValue(
      calculationZoneMap,
      basket,
      CALCULATION_ZONE_CONFIG.CALC_END_WEEK_DAY,
      true,
    ),
    calculation_eod_time: getZoneValue(
      calculationZoneMap,
      basket,
      CALCULATION_ZONE_CONFIG.CALC_EOD_TIME,
    ),
    interest_rate_on_cash: basket?.additional_settings?.cash_constituent?.interest_rate_on_cash,
    timezone: getZoneValue(calculationZoneMap, basket, CALCULATION_ZONE_CONFIG.CALC_TIMEZONE),
    launch_date: basket?.launch_date,
    identifier_type: basket?.identifier_type,
    meta_reference_data: basket?.meta_reference_data,
    additional_settings: basket?.additional_settings,
    exists_cash_constituent: existsCashConstituentsInMetadata(basket?.symbol),
    decrement_value_percentage: basket?.additional_settings?.decrement_value_percentage,
  });

  const filteredParametersToShow = buildParametersToShow(selectedBasket);
  const previousFilteredParametersToShow = selectedPreviousBasket
    ? buildParametersToShow(selectedPreviousBasket)
    : null;
  const shouldShowDiff = !isSubmitBasket && Boolean(selectedPreviousBasket);
  const rows = filteredParametersToShow?.meta_reference_data ?? [];
  const columns = Object.keys(rows?.[0] ?? {}).filter(item => {
    if (filteredParametersToShow?.identifier_type === IDENTIFIER_TYPES.ISIN) {
      return (
        item !== IDENTIFIER_TYPES.TYPE &&
        item !== IDENTIFIER_TYPES.SEDOL &&
        item !== 'iwf' &&
        item !== 'awf'
      );
    }
    return item !== IDENTIFIER_TYPES.TYPE && item !== 'iwf' && item !== 'awf';
  });

  const updatedRows = selectedUpdatedBasket?.meta_reference_data ?? [];
  const updatedColumns = Object.keys(updatedRows?.[0] ?? {}).filter(item => {
    if (selectedUpdatedBasket?.identifier_type === IDENTIFIER_TYPES.ISIN) {
      return item !== IDENTIFIER_TYPES.SEDOL && item !== 'iwf' && item !== 'awf';
    }
    return item !== 'iwf' && item !== 'awf';
  });

  return (
    <BitaModal
      isModalOpen={showModal}
      setModalState={handleCloseModal}
      customStyle={{ width: '80%' }}
    >
      <div className={styles.previewSummaryModalContainer}>
        <div className={styles.previewSummaryModalHeader}>
          <div className={styles.previewSummaryModalTitle}>SUMMARY</div>
        </div>

        <div className={styles.previewSummaryModalContent}>
          <div className={styles.previewSummaryModalTabsButtonContainer}>
            <button
              className={`${styles.tabButton} ${
                selectedTab === INITIAL_INPUTS ? styles.enabledTabButton : styles.disabledTabButton
              }`}
              onClick={() => setSelectedTab(INITIAL_INPUTS)}
            >
              1. INITIAL INPUTS
            </button>

            <button
              className={`${styles.tabButton} ${
                selectedTab === PARAMETERS ? styles.enabledTabButton : styles.disabledTabButton
              }`}
              onClick={() => setSelectedTab(PARAMETERS)}
            >
              2. PARAMETERS
            </button>

            <button
              className={`${styles.tabButton} ${
                selectedTab === ADDITIONAL_PARAMETERS
                  ? styles.enabledTabButton
                  : styles.disabledTabButton
              }`}
              onClick={() => setSelectedTab(ADDITIONAL_PARAMETERS)}
            >
              3. ADDITIONAL PARAMETERS
            </button>
          </div>

          <div className={styles.previewSummaryModalIndexesContainer}>
            <IndexSlider
              indexes={selectedBasketsData ?? []}
              selectedIndex={selectedIndex}
              setSelectedIndex={handleSetSelectedIndex}
            />
          </div>
          <div className={styles.previewSummaryModalContentContainer}>
            {selectedTab === INITIAL_INPUTS && (
              <SummaryTables
                isSubmitBasket={isSubmitBasket}
                rows={rows}
                columns={columns}
                updatedRows={updatedRows}
                updatedColumns={updatedColumns}
              />
            )}

            {selectedTab === PARAMETERS && (
              <div className={style.contentNoMetadata} style={{ width: '100%' }}>
                <div className={style.tableNoMetadata}>
                  <ShowPreview
                    isAnnualizedDailyDecrement={
                      selectedBasket?.additional_settings?.annualized_daily_decrement
                    }
                    withholdingTaxTemplateData={withholdingTaxTemplateData}
                    data={filteredParametersToShow}
                    previousData={shouldShowDiff ? previousFilteredParametersToShow : null}
                    orgPrefix={filteredParametersToShow?.orgPrefix || ''}
                    launchDate={filteredParametersToShow?.launch_date || ''}
                    automaticallyAdjustDivisor={selectedBasket?.automatically_adjust_divisor}
                    calculationZoneMap={calculationZoneMap}
                  />
                </div>
              </div>
            )}

            {selectedTab === ADDITIONAL_PARAMETERS && (
              <div className={style.contentNoMetadata} style={{ width: '100%' }}>
                <div className={style.tableNoMetadata}>
                  <ShowAdditionalParametersPreview
                    calculationType={filteredParametersToShow?.return_type}
                    additionalSettings={filteredParametersToShow?.additional_settings}
                    previousAdditionalSettings={
                      shouldShowDiff ? selectedPreviousBasket?.additional_settings : null
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.previewSummaryModalFooter}>
          <BitaButton
            className={styles.previewSummaryModalCancelButton}
            width="10%"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </BitaButton>

          <BitaButton
            className={`${styles.previewSummaryModalConfirmButton} ${
              isLoading ? styles.previewSummaryModalConfirmButtonDisabled : ''
            }`}
            width="10%"
            onClick={handleConfirm}
          >
            {isLoading ? <BitaSpinner pointers /> : 'Confirm'}
          </BitaButton>
        </div>
      </div>
    </BitaModal>
  );
};

export default PreviewSummaryModal;
